import { c as _c } from "react/compiler-runtime";
import React, { memo } from "react";
import { TextTrait } from "./TextTrait";
import { NumberTrait } from "./NumberTrait";
import { DropdownTrait } from "./DropdownTrait";
import { BooleanTrait } from "./BooleanTrait";
import type { FieldDefinition } from "./schema";
import { FieldType } from "./schema";
import type { TraitsData } from "../submission/types/TraitsData";
interface TraitFieldProps {
  readonly definition: FieldDefinition;
  readonly traits: TraitsData;
  readonly updateText: (field: keyof TraitsData, value: string) => void;
  readonly updateNumber: (field: keyof TraitsData, value: number) => void;
  readonly updateBoolean: (field: keyof TraitsData, value: boolean) => void;
  readonly error?: string | null | undefined;
  readonly onBlur?: (() => void) | undefined;
}

// Create the component
const TraitFieldComponent: React.FC<TraitFieldProps> = t0 => {
  const $ = _c(34);
  const {
    definition,
    traits,
    updateText,
    updateNumber,
    updateBoolean,
    error,
    onBlur
  } = t0;
  if (definition.type === FieldType.TEXT) {
    let t1;
    if ($[0] !== definition.field || $[1] !== definition.label || $[2] !== definition.placeholder || $[3] !== definition.readOnly || $[4] !== error || $[5] !== onBlur || $[6] !== traits || $[7] !== updateText) {
      t1 = <TextTrait label={definition.label} field={definition.field} traits={traits} updateText={updateText} readOnly={definition.readOnly} placeholder={definition.placeholder} error={error} onBlur={onBlur} />;
      $[0] = definition.field;
      $[1] = definition.label;
      $[2] = definition.placeholder;
      $[3] = definition.readOnly;
      $[4] = error;
      $[5] = onBlur;
      $[6] = traits;
      $[7] = updateText;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    return t1;
  }
  if (definition.type === FieldType.NUMBER) {
    let t1;
    if ($[9] !== definition.field || $[10] !== definition.label || $[11] !== definition.max || $[12] !== definition.min || $[13] !== definition.readOnly || $[14] !== error || $[15] !== onBlur || $[16] !== traits || $[17] !== updateNumber) {
      t1 = <NumberTrait label={definition.label} field={definition.field} traits={traits} updateNumber={updateNumber} readOnly={definition.readOnly} min={definition.min} max={definition.max} error={error} onBlur={onBlur} />;
      $[9] = definition.field;
      $[10] = definition.label;
      $[11] = definition.max;
      $[12] = definition.min;
      $[13] = definition.readOnly;
      $[14] = error;
      $[15] = onBlur;
      $[16] = traits;
      $[17] = updateNumber;
      $[18] = t1;
    } else {
      t1 = $[18];
    }
    return t1;
  }
  if (definition.type === FieldType.DROPDOWN) {
    let t1;
    if ($[19] !== definition.field || $[20] !== definition.label || $[21] !== definition.options || $[22] !== error || $[23] !== onBlur || $[24] !== traits || $[25] !== updateText) {
      t1 = <DropdownTrait label={definition.label} field={definition.field} traits={traits} updateText={updateText} options={definition.options} error={error} onBlur={onBlur} />;
      $[19] = definition.field;
      $[20] = definition.label;
      $[21] = definition.options;
      $[22] = error;
      $[23] = onBlur;
      $[24] = traits;
      $[25] = updateText;
      $[26] = t1;
    } else {
      t1 = $[26];
    }
    return t1;
  }
  if (definition.type === FieldType.BOOLEAN) {
    let t1;
    if ($[27] !== definition.field || $[28] !== definition.label || $[29] !== error || $[30] !== onBlur || $[31] !== traits || $[32] !== updateBoolean) {
      t1 = <BooleanTrait label={definition.label} field={definition.field} traits={traits} updateBoolean={updateBoolean} error={error} onBlur={onBlur} />;
      $[27] = definition.field;
      $[28] = definition.label;
      $[29] = error;
      $[30] = onBlur;
      $[31] = traits;
      $[32] = updateBoolean;
      $[33] = t1;
    } else {
      t1 = $[33];
    }
    return t1;
  }
  return null;
};

// Create comparison function for memoization
const arePropsEqual = (prevProps: TraitFieldProps, nextProps: TraitFieldProps) => {
  const {
    definition,
    traits,
    error
  } = prevProps;
  const {
    definition: nextDefinition,
    traits: nextTraits,
    error: nextError
  } = nextProps;

  // Check if field value has changed
  const fieldMatches = traits[definition.field] === nextTraits[nextDefinition.field];

  // For title/description fields, we want to re-render even if they seem the same
  // This ensures that stale text doesn't persist in the UI
  if (definition.field === "title" || definition.field === "description") {
    return false; // Always re-render title/description fields
  }

  // Check if definition changed
  const definitionMatches = definition.type === nextDefinition.type && definition.field === nextDefinition.field && definition.label === nextDefinition.label;

  // Check if validation error changed
  const errorMatches = error === nextError;

  // Special handling for dropdown fields - re-render more aggressively
  if (definition.type === FieldType.DROPDOWN) {
    // Check if any important title/description changed that might affect dropdown behavior
    if (traits.title !== nextTraits.title || traits.description !== nextTraits.description) {
      return false; // Re-render if title/description changed
    }
  }

  // Return true if nothing relevant changed (prevent re-render)
  return fieldMatches && definitionMatches && errorMatches;
};

// Export memoized component with display name
export const TraitField = memo(TraitFieldComponent, arePropsEqual);
TraitField.displayName = "TraitField";